import { useState } from 'react';
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlignLeft,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  GripVertical,
  Library,
  Plus,
  Repeat,
  Trash2,
  Wrench,
  X,
} from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Switch } from '@/components/atoms/switch';
import { Textarea } from '@/components/atoms/textarea';
import {
  CollapsibleSectionHeader,
  FormAccordionCard,
  FormOptionalSectionDivider,
} from '@/components/molecules';
import { QuestionBankPicker } from './QuestionBankPicker';
import { useJobTypes } from '@/hooks/settings/useJobTypes';
import { questionToBuilderItem } from '@/lib/forms/question-bank';
import type { BuilderIssue } from '@/lib/forms/unified-form-builder';
import type { PublicSettingsDraft } from '@/lib/forms/public-settings';
import type { FormItem, FormItemType, FormSection } from '@/types/appointmentForm';
import type { FormSurface, QuestionDefinition } from '@/types/formsEngine';
import { FormDefinitionOutlineBuilder } from './FormDefinitionOutlineBuilder';

export interface FormDefinitionBuilderValue {
  name: string;
  description: string;
  sections: FormSection[];
  jobTypeIds: string[];
  bookableServiceIds: string[];
  allowMultipleSubmissions: boolean;
  blocksCompletion: boolean;
  defaultVisibility?: 'INTERNAL' | 'CUSTOMER_VISIBLE';
  /**
   * Who fills this in: the technician on the visit, or the customer. More than
   * a label: a customer reaches a form through the public route, which refuses
   * anything not opened to it, so the answer here decides whether the template
   * is reachable by them at all.
   */
  completedBy?: 'TECHNICIAN' | 'CUSTOMER';
  /** INTAKE only: the hosted page's header, consent, submit label and after-submit behaviour. */
  publicSettings?: PublicSettingsDraft;
}

export interface FormDefinitionBuilderProps {
  value: FormDefinitionBuilderValue;
  onChange: (next: FormDefinitionBuilderValue) => void;
  /**
   * Hide the visit-only controls (per-visit multiple submissions, "required to
   * complete the visit", and job-type targeting) — used for booking surveys,
   * where those concepts don't apply.
   */
  hideVisitOptions?: boolean;
  /** Selects the surface-specific capabilities shown by the shared builder. */
  surface?: FormSurface;
  /**
   * What is stopping the template being saved, each one located. The builder
   * puts them on the fields that caused them: a list above a long form is a
   * list the person pressing Save is not looking at.
   */
  issues?: BuilderIssue[];
  /** Item types this host cannot store, hidden from the palette (legacy checklists). */
  excludeTypes?: FormItemType[];
  /**
   * INTAKE, edit mode only: lets the builder's Lead source picker read the
   * template's current channel and call `PATCH .../lead-source`. Absent
   * while creating a new template — there is nothing to patch yet.
   */
  intakeLeadSource?: { templateId: string; acquisitionChannelId: string | null };
  /** The saved version being edited, shown in the header. Absent before the first save. */
  version?: number;
}

const ITEM_TYPES: { value: FormItemType; label: string }[] = [
  { value: 'TEXT', label: 'Text' },
  { value: 'CHECKBOX', label: 'Checkbox' },
  { value: 'SINGLE_SELECT', label: 'Single select' },
  { value: 'MULTI_SELECT', label: 'Multi select' },
  { value: 'IMAGE_UPLOAD', label: 'Image upload' },
];

function emptyItem(sortOrder: number): FormItem {
  return { label: '', type: 'TEXT', required: false, sortOrder };
}

function emptySection(sortOrder: number): FormSection {
  return { name: '', sortOrder, repeatable: false, items: [emptyItem(0)] };
}

export function FormDefinitionBuilder(props: FormDefinitionBuilderProps) {
  return <FormDefinitionOutlineBuilder {...props} />;
}

export function LegacyFormDefinitionBuilder({
  value,
  onChange,
  hideVisitOptions = false,
}: FormDefinitionBuilderProps) {
  const { data: jobTypes } = useJobTypes();
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [sectionsOpen, setSectionsOpen] = useState(true);
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [jobTypesOpen, setJobTypesOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set([0]));
  const [bankSectionIndex, setBankSectionIndex] = useState<number | null>(null);
  const availableItemTypes = hideVisitOptions
    ? ITEM_TYPES.filter((itemType) => itemType.value !== 'IMAGE_UPLOAD')
    : ITEM_TYPES;

  const update = (patch: Partial<FormDefinitionBuilderValue>) => onChange({ ...value, ...patch });

  const updateSection = (idx: number, patch: Partial<FormSection>) => {
    const sections = value.sections.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    update({ sections });
  };

  const addSection = () => {
    update({ sections: [...value.sections, emptySection(value.sections.length)] });
    setExpandedSections((prev) => new Set([...prev, value.sections.length]));
  };

  const removeSection = (idx: number) => {
    update({
      sections: value.sections.filter((_, i) => i !== idx).map((s, i) => ({ ...s, sortOrder: i })),
    });
    setExpandedSections((prev) => {
      const next = new Set<number>();
      prev.forEach((n) => {
        if (n < idx) next.add(n);
        else if (n > idx) next.add(n - 1);
      });
      return next;
    });
  };

  const updateItem = (sIdx: number, iIdx: number, patch: Partial<FormItem>) => {
    const section = value.sections[sIdx];
    const items = section.items.map((it, i) => (i === iIdx ? { ...it, ...patch } : it));
    updateSection(sIdx, { items });
  };

  const addItem = (sIdx: number) => {
    const section = value.sections[sIdx];
    updateSection(sIdx, { items: [...section.items, emptyItem(section.items.length)] });
  };

  const addQuestionFromBank = (question: QuestionDefinition) => {
    if (bankSectionIndex === null) return;
    const section = value.sections[bankSectionIndex];
    if (!section) return;
    const item = questionToBuilderItem(question, section.items.length);
    if (!item) return;
    updateSection(bankSectionIndex, { items: [...section.items, item] });
    setExpandedSections((current) => new Set([...current, bankSectionIndex]));
  };

  const removeItem = (sIdx: number, iIdx: number) => {
    const section = value.sections[sIdx];
    const items = section.items
      .filter((_, i) => i !== iIdx)
      .map((it, i) => ({ ...it, sortOrder: i }));
    updateSection(sIdx, { items });
  };

  const toggleJobType = (id: string) => {
    const has = value.jobTypeIds.includes(id);
    update({
      jobTypeIds: has ? value.jobTypeIds.filter((x) => x !== id) : [...value.jobTypeIds, id],
    });
  };

  const sectionSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  function handleSectionDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = value.sections.findIndex((_, i) => `section:${i}` === active.id);
    const newIdx = value.sections.findIndex((_, i) => `section:${i}` === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(value.sections, oldIdx, newIdx).map((s, i) => ({
      ...s,
      sortOrder: i,
    }));
    update({ sections: reordered });
    setExpandedSections((prev) => {
      const next = new Set<number>();
      prev.forEach((n) => {
        if (n === oldIdx) next.add(newIdx);
        else if (oldIdx < newIdx && n > oldIdx && n <= newIdx) next.add(n - 1);
        else if (oldIdx > newIdx && n >= newIdx && n < oldIdx) next.add(n + 1);
        else next.add(n);
      });
      return next;
    });
  }

  return (
    <div className="space-y-5">
      <FormAccordionCard
        header={
          <CollapsibleSectionHeader
            icon={ClipboardList}
            label="Details"
            badge="required"
            open={detailsOpen}
            onToggle={() => setDetailsOpen((open) => !open)}
          />
        }
        open={detailsOpen}
        bodyClassName="space-y-4"
      >
        <div className="space-y-1.5">
          <Label htmlFor="form-name">Name</Label>
          <Input
            id="form-name"
            value={value.name}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="Pre-arrival checklist"
          />
        </div>
        {!hideVisitOptions && (
          <>
            <div className="flex items-start justify-between gap-3 rounded-md border bg-muted/20 p-3">
              <div className="min-w-0 flex-1">
                <Label className="text-sm font-medium">Allow multiple submissions per visit</Label>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Default off - one submission per visit. Turn on for forms that record multiple
                  events per visit, like chemical applications or equipment readings.
                </p>
              </div>
              <Switch
                checked={value.allowMultipleSubmissions}
                onCheckedChange={(checked) => update({ allowMultipleSubmissions: checked })}
              />
            </div>
            <div className="flex items-start justify-between gap-3 rounded-md border bg-muted/20 p-3">
              <div className="min-w-0 flex-1">
                <Label className="text-sm font-medium">Required to complete the visit</Label>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Default off. Turn on to block visit completion until this form has a completed
                  submission.
                </p>
              </div>
              <Switch
                checked={value.blocksCompletion}
                onCheckedChange={(checked) => update({ blocksCompletion: checked })}
              />
            </div>
          </>
        )}
      </FormAccordionCard>

      <FormAccordionCard
        header={
          <CollapsibleSectionHeader
            icon={ClipboardList}
            label="Sections"
            badge="required"
            open={sectionsOpen}
            onToggle={() => setSectionsOpen((open) => !open)}
            extra={
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-7 w-7"
                title="Add section"
                onClick={addSection}
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            }
          />
        }
        open={sectionsOpen}
        bodyClassName="space-y-3"
      >
        <DndContext
          sensors={sectionSensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleSectionDragEnd}
        >
          <SortableContext
            items={value.sections.map((_, i) => `section:${i}`)}
            strategy={verticalListSortingStrategy}
          >
            {value.sections.map((section, sIdx) => (
              <SectionCard
                key={sIdx}
                dragId={`section:${sIdx}`}
                section={section}
                sIdx={sIdx}
                expanded={expandedSections.has(sIdx)}
                onToggle={() =>
                  setExpandedSections((prev) => {
                    const next = new Set(prev);
                    next.has(sIdx) ? next.delete(sIdx) : next.add(sIdx);
                    return next;
                  })
                }
                onUpdate={(patch) => updateSection(sIdx, patch)}
                onRemove={value.sections.length > 1 ? () => removeSection(sIdx) : undefined}
                onUpdateItem={(iIdx, patch) => updateItem(sIdx, iIdx, patch)}
                onAddItem={() => addItem(sIdx)}
                onAddFromBank={() => setBankSectionIndex(sIdx)}
                onRemoveItem={(iIdx) => removeItem(sIdx, iIdx)}
                availableItemTypes={availableItemTypes}
              />
            ))}
          </SortableContext>
        </DndContext>
      </FormAccordionCard>

      <FormOptionalSectionDivider />

      <FormAccordionCard
        header={
          <CollapsibleSectionHeader
            icon={AlignLeft}
            label="Description"
            badge="optional"
            open={descriptionOpen}
            onToggle={() => setDescriptionOpen((open) => !open)}
          />
        }
        open={descriptionOpen}
        bodyClassName="space-y-3"
      >
        <Textarea
          id="form-desc"
          value={value.description}
          onChange={(e) => update({ description: e.target.value })}
          placeholder="What this form is used for"
          rows={2}
        />
      </FormAccordionCard>

      {!hideVisitOptions && (
        <FormAccordionCard
          header={
            <CollapsibleSectionHeader
              icon={Wrench}
              label="Job Types"
              badge="optional"
              open={jobTypesOpen}
              onToggle={() => setJobTypesOpen((open) => !open)}
            />
          }
          open={jobTypesOpen}
          bodyClassName="space-y-3"
        >
          <div className="space-y-1.5">
            <Label>Applies to job types</Label>
            <div className="flex flex-wrap gap-1.5">
              {jobTypes?.length ? (
                jobTypes.map((jt) => {
                  const active = value.jobTypeIds.includes(jt.id);
                  return (
                    <button
                      key={jt.id}
                      type="button"
                      onClick={() => toggleJobType(jt.id)}
                      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                        active
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border hover:bg-muted'
                      }`}
                    >
                      {jt.name}
                    </button>
                  );
                })
              ) : (
                <span className="text-xs text-muted-foreground">No job types</span>
              )}
            </div>
            {value.jobTypeIds.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Leave empty to make this form available for all job types.
              </p>
            )}
          </div>
        </FormAccordionCard>
      )}

      <QuestionBankPicker
        open={bankSectionIndex !== null}
        allowPhoto={!hideVisitOptions}
        onOpenChange={(open) => {
          if (!open) setBankSectionIndex(null);
        }}
        onSelect={addQuestionFromBank}
      />
    </div>
  );
}

// ─── Section card ────────────────────────────────────────────────────────────

interface SectionCardProps {
  dragId: string;
  section: FormSection;
  sIdx: number;
  expanded: boolean;
  onToggle: () => void;
  onUpdate: (patch: Partial<FormSection>) => void;
  onRemove?: () => void;
  onUpdateItem: (iIdx: number, patch: Partial<FormItem>) => void;
  onAddItem: () => void;
  onAddFromBank: () => void;
  onRemoveItem: (iIdx: number) => void;
  availableItemTypes: typeof ITEM_TYPES;
}

function SectionCard({
  dragId,
  section,
  expanded,
  onToggle,
  onUpdate,
  onRemove,
  onUpdateItem,
  onAddItem,
  onAddFromBank,
  onRemoveItem,
  availableItemTypes,
}: SectionCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: dragId,
  });
  const style = { transform: CSS.Transform.toString(transform), transition };

  const itemSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  function handleItemDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = section.items.findIndex((_, i) => `item:${dragId}:${i}` === active.id);
    const newIdx = section.items.findIndex((_, i) => `item:${dragId}:${i}` === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(section.items, oldIdx, newIdx).map((it, i) => ({
      ...it,
      sortOrder: i,
    }));
    onUpdate({ items: reordered });
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group/section rounded-lg border bg-card ${isDragging ? 'relative z-10 shadow-md' : ''}`}
    >
      {/* Header */}
      <div
        className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-muted/50"
        onClick={onToggle}
      >
        <button
          type="button"
          className="rounded p-1 text-muted-foreground/60 opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover/section:opacity-100"
          aria-label="Reorder section"
          onClick={(e) => e.stopPropagation()}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <input
          value={section.name}
          onChange={(e) => onUpdate({ name: e.target.value })}
          placeholder="Section name"
          onClick={(e) => e.stopPropagation()}
          className="h-7 flex-1 border-0 bg-transparent px-1 text-sm font-medium outline-none placeholder:text-muted-foreground/50 focus:ring-0"
        />
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onUpdate({ repeatable: !section.repeatable });
          }}
          className={`rounded p-1 transition-colors ${section.repeatable ? 'text-primary' : 'text-muted-foreground/40 hover:text-muted-foreground'}`}
          aria-label="Toggle repeatable"
          title="Repeatable section"
        >
          <Repeat className="h-3.5 w-3.5" />
        </button>
        <span className="text-xs text-muted-foreground">{section.items.length} fields</span>
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>

      {expanded && (
        <div className="border-t">
          <DndContext
            sensors={itemSensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleItemDragEnd}
          >
            <SortableContext
              items={section.items.map((_, i) => `item:${dragId}:${i}`)}
              strategy={verticalListSortingStrategy}
            >
              {section.items.map((item, iIdx) => (
                <ItemRow
                  key={iIdx}
                  dragId={`item:${dragId}:${iIdx}`}
                  item={item}
                  availableItemTypes={availableItemTypes}
                  onChange={(patch) => onUpdateItem(iIdx, patch)}
                  onRemove={section.items.length > 1 ? () => onRemoveItem(iIdx) : undefined}
                />
              ))}
            </SortableContext>
          </DndContext>
          <div className="flex border-t">
            <button
              type="button"
              onClick={onAddItem}
              className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-xs text-muted-foreground/60 transition-colors hover:bg-muted/40 hover:text-muted-foreground"
            >
              <Plus className="h-3 w-3" />
              Add field
            </button>
            <button
              type="button"
              onClick={onAddFromBank}
              className="flex items-center gap-2 border-l px-3 py-2 text-xs text-muted-foreground/60 transition-colors hover:bg-muted/40 hover:text-muted-foreground"
            >
              <Library className="h-3 w-3" />
              From question bank
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Item row ─────────────────────────────────────────────────────────────────

interface ItemRowProps {
  dragId: string;
  item: FormItem;
  availableItemTypes: typeof ITEM_TYPES;
  onChange: (patch: Partial<FormItem>) => void;
  onRemove?: () => void;
}

function ItemRow({ dragId, item, availableItemTypes, onChange, onRemove }: ItemRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: dragId,
  });
  const style = { transform: CSS.Transform.toString(transform), transition };

  const [descOpen, setDescOpen] = useState(false);
  const hasOptions = item.type === 'SINGLE_SELECT' || item.type === 'MULTI_SELECT';
  const [newOption, setNewOption] = useState('');
  const options = item.options ?? [];

  const addOption = () => {
    const trimmed = newOption.trim();
    if (!trimmed || options.includes(trimmed)) return;
    onChange({ options: [...options, trimmed] });
    setNewOption('');
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group/item border-b last:border-b-0 px-3 py-2 ${isDragging ? 'relative z-10 bg-muted/60 shadow-sm' : 'bg-background'}`}
    >
      {/* Compact main row */}
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          className="shrink-0 cursor-grab text-muted-foreground/40 opacity-0 transition-opacity hover:text-muted-foreground group-hover/item:opacity-100"
          aria-label="Reorder field"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>

        <input
          value={item.label}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="Field label"
          className="h-8 flex-1 min-w-0 rounded-md border border-input bg-background px-3 text-sm outline-none placeholder:text-muted-foreground/50 focus:ring-1 focus:ring-ring"
        />

        <Select
          value={item.type}
          onValueChange={(v) => {
            const next: Partial<FormItem> = { type: v as FormItemType };
            if (v !== 'SINGLE_SELECT' && v !== 'MULTI_SELECT') next.options = undefined;
            onChange(next);
          }}
        >
          <SelectTrigger className="h-8 w-32 shrink-0 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {availableItemTypes.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <label
          className="flex shrink-0 cursor-pointer items-center gap-1 text-[10px] text-muted-foreground"
          title="Required"
        >
          <Switch
            checked={item.required}
            onCheckedChange={(v) => onChange({ required: v })}
            className="scale-75"
          />
          Req
        </label>

        <button
          type="button"
          title={descOpen ? 'Hide description' : 'Add description'}
          onClick={() => setDescOpen((v) => !v)}
          className={`shrink-0 rounded p-0.5 transition-colors ${descOpen ? 'text-primary' : 'text-muted-foreground/40 hover:text-muted-foreground'}`}
        >
          <AlignLeft className="size-3.5" />
        </button>

        {onRemove ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0 text-muted-foreground"
            onClick={onRemove}
          >
            <X className="h-3 w-3" />
          </Button>
        ) : (
          <div className="h-6 w-6 shrink-0" />
        )}
      </div>

      {/* Collapsible description */}
      {descOpen && (
        <div className="mt-1.5 pl-5">
          <input
            value={(item as FormItem & { description?: string }).description ?? ''}
            onChange={(e) =>
              onChange({ ...item, description: e.target.value } as Partial<FormItem>)
            }
            placeholder="Help text for the tech…"
            className="h-7 w-full rounded border border-input bg-background px-2 text-xs outline-none placeholder:text-muted-foreground/50 focus:ring-1 focus:ring-ring"
          />
        </div>
      )}

      {/* Options for select types */}
      {hasOptions && (
        <div className="mt-2 pl-5 space-y-1.5">
          <div className="flex flex-wrap gap-1.5">
            {options.map((opt) => (
              <Badge key={opt} variant="secondary" className="gap-1 pr-1 text-xs">
                {opt}
                <button
                  type="button"
                  onClick={() => onChange({ options: options.filter((o) => o !== opt) })}
                  className="rounded-sm p-0.5 hover:bg-muted"
                  aria-label={`Remove option ${opt}`}
                >
                  <X className="size-3" />
                </button>
              </Badge>
            ))}
            {options.length === 0 && (
              <span className="text-xs text-muted-foreground">No options yet</span>
            )}
          </div>
          <div className="flex gap-2">
            <Input
              value={newOption}
              onChange={(e) => setNewOption(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addOption();
                }
              }}
              placeholder="Add option and press Enter"
              className="h-8 text-sm"
            />
            <Button type="button" size="sm" variant="outline" onClick={addOption}>
              Add
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
